import React from 'react';
import { SHADER_REGISTRY, CATEGORY_DISPLAY_NAMES } from './shader-registry';
import { ShaderCategory } from './shader-types';

// Import all preview components
import { FirePreview } from './effects/fire';
import { SlimeballPreview } from './effects/slimeball';
import { FireballPreview } from './effects/fireball';
import { SpinningCubePreview } from './objects/spinningcube';
import { WaterWavePreview } from './simulations/waterwave';
import { SnowfallPreview } from './simulations/snowfall';

// Map shader names to their preview components
const PREVIEW_COMPONENTS = {
  fire: FirePreview,
  slimeball: SlimeballPreview,
  fireball: FireballPreview,
  spinningcube: SpinningCubePreview,
  waterwave: WaterWavePreview,
  snowfall: SnowfallPreview,
} as const;

export interface ShaderPickerItem {
  itemId: string;
  itemName: string;
  itemRender: React.ComponentType<any>;
  description?: string;
  tags?: string[];
  disabled?: boolean;
}

export interface ShaderPickerCategory {
  categoryName: string;
  items: ShaderPickerItem[];
}

/**
 * Convert shader registry to format expected by ShaderPickerModal
 */
export function getShaderPickerData(): ShaderPickerCategory[] {
  const categories: ShaderPickerCategory[] = [];

  for (const [categoryKey, shaders] of Object.entries(SHADER_REGISTRY)) {
    if (shaders.length === 0) continue;

    const category: ShaderPickerCategory = {
      categoryName: CATEGORY_DISPLAY_NAMES[categoryKey as ShaderCategory],
      items: shaders.map((entry) => {
        // Create a sample instance to get the name
        const sampleInstance = new entry.constructor();
        const shaderName = sampleInstance.name;
        const PreviewComponent =
          PREVIEW_COMPONENTS[shaderName as keyof typeof PREVIEW_COMPONENTS];

        return {
          itemId: shaderName,
          itemName: formatDisplayName(shaderName),
          itemRender: () => (
            <PreviewComponent model={createPreviewShader(entry.constructor)} />
          ),
          description: entry.metadata.description,
          tags: entry.metadata.tags,
        };
      }),
    };

    categories.push(category);
  }

  return categories;
}

/**
 * Format shader name for display (e.g., "waterwave" -> "Water Wave")
 */
function formatDisplayName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before capitals
    .split(/[\s_-]+/) // Split on spaces, underscores, hyphens
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Create a default shader instance with preview-friendly dimensions
 */
export function createPreviewShader(ShaderConstructor: any) {
  return new ShaderConstructor({
    width: 200,
    height: 150,
    referenceWidth: 200,
    referenceHeight: 150,
  });
}
